import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Hook para obtener el rol del usuario autenticado
 * Extrae el rol de los claims del token JWT
 */
export const useUserRole = () => {
  const [role, setRole] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getUserRole = async () => {
      try {
        setLoading(true);
        const session = await fetchAuthSession({ forceRefresh: true });

        if (session && session.tokens) {
          // Leer grupos del Access Token
          const accessPayload = session.tokens.accessToken?.payload || {};
          const cognitoGroups = accessPayload['cognito:groups'] || [];
          setGroups(cognitoGroups);

          // Leer custom:role del ID Token (donde están los custom attributes)
          const idPayload = session.tokens.idToken?.payload || {};

          // El primer grupo es el rol principal (por precedencia)
          const primaryRole = cognitoGroups.length > 0 ? cognitoGroups[0] : null;

          // Fallback a custom:role si no hay grupos
          const customRole = idPayload['custom:role'];
          const effectiveRole = (primaryRole || customRole || 'COMANDA').toUpperCase();

          setRole(effectiveRole);
        } else {
          setError('No authentication session found');
        }
      } catch (err) {
        console.error('[useUserRole] Error getting user role:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    getUserRole();
  }, []);

  /**
   * Verifica si el usuario tiene uno de los roles permitidos
   * @param {string[]} allowedRoles - Array de roles permitidos
   * @returns {boolean}
   */
  const hasRole = (allowedRoles) => {
    if (!role) return false;
    return allowedRoles.includes(role);
  };

  /**
   * Verifica si el usuario es OWNER
   * @returns {boolean}
   */
  const isOwner = () => role === 'OWNER';

  /**
   * Verifica si el usuario es ADMIN o superior
   * @returns {boolean}
   */
  const isAdmin = () => ['OWNER', 'ADMIN'].includes(role);

  /**
   * Verifica si el usuario puede acceder al admin panel
   * @returns {boolean}
   */
  const canAccessAdminPanel = () => ['OWNER', 'ADMIN'].includes(role);

  return {
    role,
    groups,
    loading,
    error,
    hasRole,
    isOwner,
    isAdmin,
    canAccessAdminPanel,
  };
};
